import { FC, useMemo, useState } from 'react';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Dialog, DialogContent, Grid, IconButton } from '@mui/material';
import { User } from '@prisma/client';

import { useMutation } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { NPIDataGrid, NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DELETE_NOTE } from 'src/graphql/mutations/notes.mutation';
import { formatDate } from 'src/utils/utils';

type UserSubset = Pick<User, 'id' | 'firstName' | 'lastName'>;
type Note = {
    id: number;
    subject: string;
    body: string;
    createdAt: string;
    createdBy: UserSubset;
};
interface NotesProps {
    notes: Note[];
    refetch: () => void;
    isMerchant?: boolean;
    maxHeight?: string;
    hideDelete?: boolean;
}

const Notes: FC<NotesProps> = ({ notes, refetch, isMerchant, maxHeight, hideDelete }) => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number>();
    const [viewNote, setViewNote] = useState<string>();

    const [deleteNote] = useMutation(DELETE_NOTE, {
        onError: () => {
            return showErrorToast('We were unable to delete the selected note');
        },
        onCompleted: () => {
            showSuccessToast('Note deleted!');
            refetch();
        },
    });

    const tableData = useMemo(() => {
        if (notes?.length) {
            return notes.map((note) => {
                const dateCreated = note?.createdAt;
                const createdBy = isMerchant
                    ? note?.createdBy
                    : `${note?.createdBy?.firstName} ${note?.createdBy?.lastName}`;

                return {
                    ...note,
                    dateCreated,
                    createdBy,
                };
            });
        }

        return [];
    }, [isMerchant, notes]);

    // Delete record callback sent to table component
    const handleOpenDeleteModal = (recordId: number) => {
        setIdToDelete(recordId);
        setOpenDeleteModal(true);
    };

    // Close the delete modal
    const handleCloseDeleteModal = () => {
        setIdToDelete(undefined);
        setOpenDeleteModal(false);
    };

    const handleOpenViewModal = (note: Note) => {
        setViewNote(note?.body);
        setOpenViewModal(true);
    };

    // Close the view modal
    const handleCloseViewModal = () => {
        setOpenViewModal(false);
    };

    const handleDeleteNote = () => {
        idToDelete && deleteNote({ variables: { id: idToDelete } });
        handleCloseDeleteModal();
    };

    const NOTES_TABLE_HEADERS: NPIDataGridColDef[] = [
        createColumnDef({
            field: 'dateCreated',
            headerName: 'Date/Time',
            renderCell: (params) => {
                const date = new Date(params?.row?.createdAt);
                return formatDate({ date });
            },
        }),
        createColumnDef({
            field: 'subject',
            headerName: 'Subject',
        }),
        createColumnDef({
            field: 'createdBy',
            headerName: 'Added By',
        }),
        // TODO: (KM) Make this generic to the NPIDataGrid
        createColumnDef({
            field: 'actions',
            headerName: '',
            flex: 0,
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <Flex gap="8px">
                        <IconButton
                            aria-label="view"
                            data-testid="view"
                            onClick={() => {
                                handleOpenViewModal(params.row);
                            }}
                        >
                            <VisibilityOutlinedIcon />
                        </IconButton>
                        {!hideDelete && (
                            <IconButton aria-label="delete" data-testid="delete">
                                <DeleteOutlineOutlinedIcon
                                    onClick={() => {
                                        return handleOpenDeleteModal(params.row.id);
                                    }}
                                />
                            </IconButton>
                        )}
                    </Flex>
                );
            },
        }),
    ];

    return (
        <Grid sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <NPIDataGrid
                dataTestId="notes-table"
                rows={tableData}
                columns={NOTES_TABLE_HEADERS}
                getRowId={(row) => {
                    return row.id;
                }}
                title="Notes"
                maxHeight={maxHeight}
            />
            <Dialog
                open={openDeleteModal}
                onClose={handleCloseDeleteModal}
                aria-labelledby="delete-note-offer-dialog"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseDeleteModal}
                        handleAction={handleDeleteNote}
                        header="Delete Note?"
                        message="Are you sure you want to permanently delete this note? This transaction cannot be restored once deleted"
                        buttonText="Delete"
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={openViewModal}
                onClose={handleCloseViewModal}
                aria-labelledby="view-note-offer-dialog"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseViewModal}
                        isViewModal
                        isActionDisabled
                        header="View Note"
                        message={viewNote}
                        closeModalButtonText="Close"
                        handleAction={() => {}}
                    />
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default Notes;
